import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import {
  DocumentArrowDownIcon,
  ChartBarIcon,
  CreditCardIcon,
  DocumentTextIcon,
  ArrowTrendingUpIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';

const PayrollDashboard = () => {
  const navigate = useNavigate();
  const [selectedPeriod, setSelectedPeriod] = useState('current');

  // Mock payroll data
  const currentPayslip = {
    month: 'July 2025',
    basicSalary: 85000,
    allowances: 15000,
    overtime: 3500,
    deductions: 12500,
    netSalary: 91000,
    status: 'Processed',
    payDate: '2025-07-31'
  };

  const ytdSummary = {
    grossEarnings: 650000,
    totalDeductions: 87500,
    netEarnings: 562500,
    taxDeducted: 52000,
    pf: 24000,
    esi: 4500
  };

  const recentPayslips = [
    { month: 'July 2025', amount: 91000, status: 'Processed', date: '2025-07-31' },
    { month: 'June 2025', amount: 89500, status: 'Processed', date: '2025-06-30' },
    { month: 'May 2025', amount: 88200, status: 'Processed', date: '2025-05-31' },
    { month: 'April 2025', amount: 87800, status: 'Processed', date: '2025-04-30' },
    { month: 'March 2025', amount: 86500, status: 'Processed', date: '2025-03-31' }
  ];

  const benefits = [
    { name: 'Health Insurance', value: '₹50,000', status: 'Active', coverage: 'Family' },
    { name: 'Life Insurance', value: '₹10,00,000', status: 'Active', coverage: 'Individual' },
    { name: 'Provident Fund', value: '12% of Basic', status: 'Active', coverage: 'Mandatory' },
    { name: 'Gratuity', value: '4.81% of Basic', status: 'Active', coverage: 'Eligible' }
  ];

  const quickActions = [
    {
      name: 'Download Payslip',
      description: 'Get current month payslip',
      icon: DocumentArrowDownIcon,
      action: () => navigate('/payroll/payslip'),
      color: 'bg-gradient-to-br from-blue-500 to-blue-600'
    },
    {
      name: 'Tax Documents',
      description: 'Form 16, TDS certificates',
      icon: DocumentTextIcon,
      action: () => navigate('/payroll/tax-documents'),
      color: 'bg-gradient-to-br from-green-500 to-green-600'
    },
    {
      name: 'Benefits Overview',
      description: 'View all benefits & coverage',
      icon: CreditCardIcon,
      action: () => navigate('/payroll/benefits'),
      color: 'bg-gradient-to-br from-purple-500 to-purple-600'
    },
    {
      name: 'Salary History',
      description: 'View historical salary data',
      icon: ChartBarIcon,
      action: () => navigate('/payroll/history'),
      color: 'bg-gradient-to-br from-orange-500 to-orange-600'
    }
  ];

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Processed': return 'text-green-600 bg-green-100';
      case 'Pending': return 'text-yellow-600 bg-yellow-100';
      case 'Failed': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  return (
    <div className="min-h-screen bg-apollo-gray">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        {/* Header Section */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Payroll Dashboard</h1>
              <p className="mt-1 sm:mt-2 text-base sm:text-lg text-gray-600">Manage your salary, benefits, and tax documents</p>
            </div>
            <div className="w-full sm:w-auto">
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="block w-full sm:w-auto pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-apollo-primary focus:border-apollo-primary rounded-md"
              >
                <option value="current">Current Month</option>
                <option value="ytd">Year to Date</option>
                <option value="previous">Previous Month</option>
              </select>
            </div>
          </div>
        </div>

        {/* Current Payslip Overview */}
        <div className="mb-6 sm:mb-8">
          <div className="bg-white overflow-hidden shadow-lg rounded-2xl border border-gray-200">
            <div className="bg-gradient-to-r from-apollo-primary to-apollo-accent px-4 sm:px-6 py-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-2 sm:space-y-0">
                <div>
                  <h3 className="text-lg font-semibold text-white">Current Payslip</h3>
                  <p className="text-apollo-primary-100">{currentPayslip.month}</p>
                </div>
                <div className="text-left sm:text-right">
                  <div className="text-xl sm:text-2xl font-bold text-white">{formatCurrency(currentPayslip.netSalary)}</div>
                  <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(currentPayslip.status)}`}>
                    <CheckCircleIcon className="w-3 h-3 mr-1" />
                    {currentPayslip.status}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
                <div className="text-center">
                  <div className="text-lg sm:text-2xl font-bold text-gray-900">{formatCurrency(currentPayslip.basicSalary)}</div>
                  <div className="text-sm text-gray-500">Basic Salary</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-2xl font-bold text-green-600">{formatCurrency(currentPayslip.allowances)}</div>
                  <div className="text-sm text-gray-500">Allowances</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-2xl font-bold text-blue-600">{formatCurrency(currentPayslip.overtime)}</div>
                  <div className="text-sm text-gray-500">Overtime</div>
                </div>
                <div className="text-center">
                  <div className="text-lg sm:text-2xl font-bold text-red-600">-{formatCurrency(currentPayslip.deductions)}</div>
                  <div className="text-sm text-gray-500">Deductions</div>
                </div>
              </div>
              
              <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-2 sm:space-y-0">
                  <span className="text-base sm:text-lg font-medium text-gray-900">Next Pay Date:</span>
                  <span className="text-base sm:text-lg font-bold text-apollo-primary">{currentPayslip.payDate}</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mb-6 sm:mb-8">
          <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
            {quickActions.map((action, index) => (
              <div
                key={index}
                onClick={action.action}
                className="group cursor-pointer transform transition-all duration-300 hover:scale-105"
              >
                <div className={`${action.color} rounded-2xl p-4 sm:p-6 text-white shadow-lg hover:shadow-xl`}>
                  <div className="flex items-center justify-between mb-4">
                    <action.icon className="h-6 w-6 sm:h-8 sm:w-8" />
                    <div className="w-3 h-3 bg-white/20 rounded-full group-hover:scale-150 transition-transform duration-300"></div>
                  </div>
                  <h4 className="text-base sm:text-lg font-semibold mb-2">{action.name}</h4>
                  <p className="text-sm opacity-90">{action.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8">
          {/* Year to Date Summary */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">Year to Date Summary</h3>
              <ArrowTrendingUpIcon className="h-5 w-5 sm:h-6 sm:w-6 text-green-500" />
            </div>
            
            <div className="space-y-3 sm:space-y-4">
              <div className="flex justify-between items-center p-3 sm:p-4 bg-gray-50 rounded-lg">
                <span className="font-medium text-gray-700 text-sm sm:text-base">Gross Earnings</span>
                <span className="text-base sm:text-lg font-bold text-gray-900">{formatCurrency(ytdSummary.grossEarnings)}</span>
              </div>
              <div className="flex justify-between items-center p-3 sm:p-4 bg-gray-50 rounded-lg">
                <span className="font-medium text-gray-700 text-sm sm:text-base">Total Deductions</span>
                <span className="text-base sm:text-lg font-bold text-red-600">-{formatCurrency(ytdSummary.totalDeductions)}</span>
              </div>
              <div className="flex justify-between items-center p-3 sm:p-4 bg-green-50 rounded-lg border border-green-200">
                <span className="font-medium text-green-700 text-sm sm:text-base">Net Earnings</span>
                <span className="text-lg sm:text-xl font-bold text-green-600">{formatCurrency(ytdSummary.netEarnings)}</span>
              </div>
              
              <div className="pt-3 sm:pt-4 border-t border-gray-200">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4 text-center">
                  <div className="flex justify-between sm:block">
                    <div className="text-sm text-gray-500">Tax Deducted</div>
                    <div className="font-semibold text-gray-900 text-sm sm:text-base">{formatCurrency(ytdSummary.taxDeducted)}</div>
                  </div>
                  <div className="flex justify-between sm:block">
                    <div className="text-sm text-gray-500">PF Contribution</div>
                    <div className="font-semibold text-gray-900 text-sm sm:text-base">{formatCurrency(ytdSummary.pf)}</div>
                  </div>
                  <div className="flex justify-between sm:block">
                    <div className="text-sm text-gray-500">ESI</div>
                    <div className="font-semibold text-gray-900 text-sm sm:text-base">{formatCurrency(ytdSummary.esi)}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Benefits Overview */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-4 sm:p-6">
            <div className="flex items-center justify-between mb-4 sm:mb-6">
              <h3 className="text-lg sm:text-xl font-bold text-gray-900">Employee Benefits</h3>
              <CreditCardIcon className="h-5 w-5 sm:h-6 sm:w-6 text-purple-500" />
            </div>
            
            <div className="space-y-3 sm:space-y-4">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center justify-between p-3 sm:p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200">
                  <div>
                    <div className="font-medium text-gray-900">{benefit.name}</div>
                    <div className="text-sm text-gray-500">{benefit.coverage}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold text-gray-900">{benefit.value}</div>
                    <div className={`text-xs px-2 py-1 rounded-full ${getStatusColor(benefit.status)}`}>
                      {benefit.status}
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-6 pt-6 border-t border-gray-200">
              <button
                onClick={() => navigate('/payroll/benefits')}
                className="w-full bg-purple-50 text-purple-600 hover:bg-purple-100 transition-colors duration-200 px-4 py-2 rounded-lg font-medium"
              >
                View All Benefits
              </button>
            </div>
          </div>
        </div>

        {/* Recent Payslips */}
        <div className="mt-8">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Recent Payslips</h3>
              <button
                onClick={() => navigate('/payroll/history')}
                className="text-apollo-primary hover:text-apollo-secondary font-medium"
              >
                View All →
              </button>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Month</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Pay Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {recentPayslips.map((payslip, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{payslip.month}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{formatCurrency(payslip.amount)}</td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(payslip.status)}`}>
                          {payslip.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{payslip.date}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button className="text-apollo-primary hover:text-apollo-secondary">Download</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PayrollDashboard;
